/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useWorkspaceData, useGlobalOrders } from "@data";
import { Inline } from "@design-system";
import {
  LocationModal,
  PageLayout,
  TableSkeleton,
  InfoMessageAtom,
  Button,
  InstantSearch,
  EmptyState,
  LocationsList,
  HelpLabel,
  StoryblokHelpBlock,
  SingleSelectRadioFilterDropdown,
  SingleSelectFilterOption,
  ClearFiltersButton,
  CopyLinkButton,
} from "@components";
import { Toast } from "flowbite-react";
import {
  HiOutlineX,
  HiOutlineUser,
  HiCheck,
  HiOutlineFilter,
} from "react-icons/hi";
import { emptyOrdersIllustration } from "../../components/Icons/illustrations";
import { Location } from "../../models";
import { useCache } from "../../context/CacheContext";
import { getLocationIdsFromOrders } from "../../helpers/orderLocationHelpers";
import { buildShareableUrl } from "../../helpers/shareableUrl";

export const Locations: FC = function () {
  const { t } = useTranslation(["common", "orders", "validation", "location"]);
  const { activeWorkspace } = useWorkspace();
  const [splitIsOpen, setSplitIsOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isInsertModalOpen, openInsertModal] = useState(false);
  const [locationData, setLocationData] = useState(Location.defaultLocation());
  const { updateCacheKey } = useCache();

  // Filter states
  const [driverFilter, setDriverFilter] = useState<string | undefined>(
    undefined,
  );
  const [searchText, setSearchText] = useState<string>("");
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);

  // Dropdown states to control opening/closing
  const [driverDropdownOpen, setDriverDropdownOpen] = useState(false);

  // State for dropdown positioning
  const [dropdownPosition, setDropdownPosition] = useState<"left" | "right">(
    "left",
  );

  // State for hiding driver filter info box
  const [hideDriverFilterInfo, setHideDriverFilterInfo] = useState(false);

  // Refs for dropdown elements
  const driverDropdownRef = useRef<HTMLDivElement>(null);

  // Update dropdown position when it opens or window resizes
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (driverDropdownOpen && driverDropdownRef.current) {
        const rect = driverDropdownRef.current.getBoundingClientRect();
        const spaceOnRight = window.innerWidth - rect.right;

        // If there's not enough space on the right, position dropdown to the left
        if (spaceOnRight < 240) {
          setDropdownPosition("right");
        } else {
          setDropdownPosition("left");
        }
      }
    };

    // Initial position check
    updateDropdownPosition();

    // Add resize listener
    window.addEventListener("resize", updateDropdownPosition);

    // Cleanup
    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [driverDropdownOpen]);

  // Drivers and locations come from the centrally-loaded workspace data store
  // (fetched once when the app loads, shared across the whole app).
  const { drivers, locations, loading } = useWorkspaceData();

  // Reuse the already-loaded global orders instead of issuing a separate query.
  // Only the selected driver's orders are needed (to map driver -> locations).
  const { data: allOrders } = useGlobalOrders();
  const driverOrders = useMemo(
    () =>
      driverFilter
        ? allOrders.filter((order) => order.driverId === driverFilter)
        : [],
    [allOrders, driverFilter],
  );

  // Logic to handle adding or updating locations
  const handleCreateOrUpdate = async (location: Location) => {
    // Validate workspaceId
    const workspaceId = activeWorkspace?.workspaceId;
    if (!workspaceId) {
      console.error(
        "Workspace ID is undefined. Cannot create or update location.",
      );
      return false;
    }

    const isNewLocation = Location.isNew(locationData);

    if (isNewLocation) {
      const created = await Location.create({
        ...location,
        workspaceId, // Now guaranteed to be a string
      });
      if (!created) return false;
    } else {
      try {
        await Location.update(locationData, {
          ...location,
          workspaceId, // Now guaranteed to be a string
        });
      } catch (error) {
        console.error("Error updating location:", error);
        return false;
      }
    }

    updateCacheKey(); // Busts the cached locations query -> store refreshes
    openInsertModal(false); // Close modal after action
    return true;
  };

  // Initialize filters from URL parameters when component mounts
  useEffect(() => {
    // Get driver filter from URL
    const driverParam = searchParams.get("driver");
    if (driverParam) {
      setDriverFilter(driverParam);
    }

    // Get search text from URL
    const searchParam = searchParams.get("search");
    if (searchParam) {
      setSearchText(searchParam);
    }
  }, []); // Kjør kun ved montering

  // Update URL when filters change
  useEffect(() => {
    const newSearchParams = new URLSearchParams();

    // Preserve the location ID if it exists
    const locationParam = searchParams.get("location");
    if (locationParam) {
      newSearchParams.set("location", locationParam);
    }

    // Update driver parameter
    if (driverFilter) {
      newSearchParams.set("driver", driverFilter);
    }

    // Update search parameter
    if (searchText) {
      newSearchParams.set("search", searchText);
    }

    // Update URL without reloading the page
    setSearchParams(newSearchParams, { replace: true });
  }, [driverFilter, searchText, setSearchParams]);

  // Combine all loading states into one, but only for essential data
  const isLoading = loading.locations || loading.drivers;

  // Client-side filtering - exactly like drivers page
  useEffect(() => {
    let filtered = [...(locations || [])];

    // Apply search text filter (client-side for all fields)
    if (searchText.trim().length > 0) {
      filtered = Location.filterBySearchText(filtered, searchText);
    }

    // Apply driver filter if selected
    if (driverFilter && driverOrders.length > 0) {
      const locationIds = getLocationIdsFromOrders(driverOrders);
      filtered = filtered.filter((location) =>
        locationIds.includes(location.id),
      );
    }

    setFilteredLocations(filtered);
  }, [locations, searchText, driverFilter, driverOrders]);

  // Determine if content is ready to be displayed
  const isContentReady = !isLoading;

  // Handle click outside to close dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Close driver dropdown if click is outside
      if (
        driverDropdownRef.current &&
        !driverDropdownRef.current.contains(event.target as Node) &&
        driverDropdownOpen
      ) {
        setDriverDropdownOpen(false);
      }
    }

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [driverDropdownOpen]);

  // Handle driver filter change
  const handleDriverFilterChange = (driverId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from propagating

    // Hvis samme driver er valgt, fjern filteret (toggle off)
    // Ellers, sett filteret til den valgte driveren (single select)
    if (driverFilter === driverId) {
      setDriverFilter(undefined);
    } else {
      setDriverFilter(driverId);
      setHideDriverFilterInfo(false); // Reset the hide state when changing filter
    }

    // Lukk dropdown etter valg
    setDriverDropdownOpen(false);
  };

  // Clear all filters
  const clearFilters = () => {
    setDriverFilter(undefined);
    setSearchText("");
    setHideDriverFilterInfo(false); // Reset the hide state when clearing filters

    // Preserve the location ID when clearing filters
    const locationParam = searchParams.get("location");
    if (locationParam) {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set("location", locationParam);
      setSearchParams(newSearchParams, { replace: true });
    } else {
      // Hvis det ikke er noen location-parameter, fjern alle parametere
      setSearchParams({}, { replace: true });
    }
  };

  // Custom dropdown toggle handler
  const toggleDriverDropdown = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from propagating to elements behind
    // Bruk setTimeout for å forhindre at ResizeObserver-hendelser stables opp
    setTimeout(() => {
      setDriverDropdownOpen(!driverDropdownOpen);
    }, 10);
  };

  // State for toast notification
  const [showLinkCopiedToast, setShowLinkCopiedToast] = useState(false);

  // Function to generate a shareable link with current filters
  const getShareableLink = () =>
    buildShareableUrl([
      ["location", searchParams.get("location")],
      ["driver", driverFilter],
      ["search", searchText || undefined],
    ]);

  // Function to copy shareable link to clipboard
  const copyShareableLink = () => {
    const link = getShareableLink();
    navigator.clipboard
      .writeText(link)
      .then(() => {
        // Show toast notification
        setShowLinkCopiedToast(true);
        // Hide toast after 3 seconds
        setTimeout(() => setShowLinkCopiedToast(false), 3000);
      })
      .catch((err) => {
        console.error("Failed to copy link: ", err);
      });
  };

  const hasLocations = filteredLocations.length > 0;
  const hasAnyLocations = locations.length > 0;
  const hasActiveFilters = driverFilter !== undefined || searchText.length > 0;

  const driverFilterOptions: SingleSelectFilterOption[] = drivers.map(
    (driver) => ({
      key: driver.email,
      label: `${driver.firstName} ${driver.lastName}`,
    }),
  );

  return (
    <PageLayout>
      {/* Toast notification for link copied */}
      {showLinkCopiedToast && (
        <div className="fixed bottom-5 right-5 z-50">
          <Toast>
            <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-100 text-green-500 dark:bg-green-800 dark:text-green-200">
              <HiCheck className="h-5 w-5" />
            </div>
            <div className="ml-3 text-sm font-normal">
              {t("common:link_copied")}
            </div>
            <button
              type="button"
              className="ml-auto -mx-1.5 -my-1.5 bg-white text-muted hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700"
              onClick={() => setShowLinkCopiedToast(false)}
              aria-label="Close"
            >
              <span className="sr-only">Close</span>
              <HiOutlineX className="w-5 h-5" />
            </button>
          </Toast>
        </div>
      )}

      {/* Header Section */}
      <div className="flex justify-between items-start lg:p-4">
        <div className={splitIsOpen ? "hidden lg:block" : ""}>
          <Inline gap={3}>
            <h1 className="text-xl font-semibold text-text sm:text-2xl">
              {t("location:page_title")}
            </h1>
            <HelpLabel
              content={
                <StoryblokHelpBlock
                  storySlug="logibud-tms-web/help-popups/locations"
                  accessToken="Jf9NMAu4DXeo3fwdjd1w7wtt"
                />
              }
              className="mt-1"
            />
          </Inline>
          <p className="text-secondary text-base pt-2 pb-4 dark:text-secondary">
            {t("location:page_description")}
          </p>
        </div>
      </div>

      {/* Filter bar - only show when content is ready and we have locations */}
      {isContentReady && (hasLocations || hasAnyLocations) && (
        <div className="flex flex-col md:flex-row gap-3 mb-4 px-4 flex">
          {/* Search input */}
          <InstantSearch
            placeholder={t("location:search_placeholder", {
              defaultValue: "Search for name, address, city or postal code...",
            })}
            value={searchText}
            onChange={setSearchText}
          />

          <div className="flex gap-3 items-center">
            {/* Driver filter dropdown */}
            <SingleSelectRadioFilterDropdown
              containerRef={driverDropdownRef}
              isOpen={driverDropdownOpen}
              onToggle={toggleDriverDropdown}
              icon={<HiOutlineUser className="mr-2 h-5 w-5" />}
              label={t("common:driver")}
              labelClassName=""
              justifyClassName="justify-between"
              options={driverFilterOptions}
              selectedKey={driverFilter}
              onOptionClick={(key, e) => handleDriverFilterChange(key, e)}
              headerTitle={t("common:drivers")}
              radioName="driverFilter"
              onClear={() => setDriverFilter(undefined)}
              panelStyle={{
                width: "240px",
                height: "auto",
                minHeight: "200px",
                maxHeight: "300px",
                right: dropdownPosition === "right" ? 0 : "auto",
              }}
            />
            <Button
              variant="add"
              onClick={() => {
                setLocationData(Location.defaultLocation());
                openInsertModal(true);
              }}
            >
              {t("location:new_location_button", {
                defaultValue: "New location",
              })}
            </Button>

            {/* Clear filters button */}
            {hasActiveFilters && (
              <ClearFiltersButton
                onClick={clearFilters}
                title={t("common:clear_filters")}
              />
            )}

            {/* Copy link button */}
            {hasActiveFilters && (
              <CopyLinkButton
                onClick={copyShareableLink}
                title={t("common:copy_link")}
              />
            )}
          </div>
        </div>
      )}

      {/* Driver filter info box */}
      {isContentReady && driverFilter && !hideDriverFilterInfo && (
        <div className="mx-4 mb-4">
          <InfoMessageAtom
            variant="info"
            title={t("location:driver_filter_info.title", {
              defaultValue: "Filtrerte lokasjoner",
            })}
            dismissible
            onDismiss={() => setHideDriverFilterInfo(true)}
          >
            <p>
              {t("location:driver_filter_info.description", {
                defaultValue:
                  "Viser kun lokasjoner som er knyttet til ordrer for sjåføren {{driver}}.",
                driver: drivers.find((d) => d.email === driverFilter)
                  ? `${drivers.find((d) => d.email === driverFilter)?.firstName} ${drivers.find((d) => d.email === driverFilter)?.lastName}`
                  : driverFilter,
              })}
            </p>
          </InfoMessageAtom>
        </div>
      )}

      {/* Empty State or Locations List */}
      {!isContentReady ? (
        <TableSkeleton />
      ) : !hasLocations ? (
        hasAnyLocations && hasActiveFilters ? (
          // Empty state for when there are locations in the workspace but none match the current filters
          <EmptyState
            icon={
              <div className="flex justify-center items-center w-20 h-20 rounded-full bg-surface-muted">
                <HiOutlineFilter className="w-10 h-10 text-muted" />
              </div>
            }
            title={t("location:empty_state.filtered.title", {
              defaultValue: "No locations match your filters",
            })}
            description={t("location:empty_state.filtered.description", {
              defaultValue:
                "Try adjusting your search or filter criteria to find what you're looking for.",
            })}
            buttonText={t("location:empty_state.filtered.button_text", {
              defaultValue: "Clear filters",
            })}
            buttonClick={clearFilters}
            buttonIcon="x"
            buttonColor="gray"
          />
        ) : (
          // Empty state for when there are no locations at all in the workspace
          <EmptyState
            icon={emptyOrdersIllustration}
            title={t("location:empty_state.general.title")}
            description={t("location:empty_state.general.description")}
            buttonText={t("location:empty_state.general.button_text")}
            buttonClick={() => {
              setLocationData(Location.defaultLocation());
              openInsertModal(true);
            }}
          />
        )
      ) : (
        <div className="px-4">
          <LocationsList
            locations={filteredLocations}
            isLoading={false}
            isOpen={setSplitIsOpen}
          />
        </div>
      )}

      {/* Location Modal */}
      <LocationModal
        data={locationData}
        isShowing={isInsertModalOpen}
        onConfirm={(location: Location) => handleCreateOrUpdate(location)}
        onCancel={() => openInsertModal(false)}
      />
    </PageLayout>
  );
};
